import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Logo } from '../Logo';

/**
 * Feb 2026 Marketing Redesign — the shared top nav for every marketing
 * page. Left: logo. Middle: nav links (How It Works, Features, Industries,
 * Pricing, About). Right: Sign In (secondary) + Start Free Trial (primary
 * red pill). Sticky with a subtle blur once the user has scrolled a few
 * pixels so the hero can breathe at the top of the page.
 */

const NAV_LINKS = [
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/features', label: 'Features' },
  { to: '/industries', label: 'Industries' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
];

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-shadow duration-200 bg-white/95 backdrop-blur-md ${
        scrolled ? 'border-b border-slate-100 shadow-[0_1px_0_rgba(11,19,43,0.03)]' : 'border-b border-transparent'
      }`}
      data-testid="marketing-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-home-link">
          <Logo linkTo={null} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-[14.5px] font-medium transition-colors ${
                  isActive
                    ? 'text-[#0B132B] bg-slate-100/70'
                    : 'text-slate-600 hover:text-[#0B132B] hover:bg-slate-100/70'
                }`
              }
              data-testid={`nav-link-${l.to.replace(/\//g, '')}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link
            to="/login"
            className="px-4 py-2 rounded-full text-[14.5px] font-medium text-slate-600 hover:text-[#0B132B] hover:bg-slate-100/70 transition-colors"
            data-testid="nav-sign-in-btn"
          >
            Sign In
          </Link>
          <Link
            to="/register?role=manager"
            className="inline-flex items-center h-11 px-5 rounded-full bg-[#DA291C] text-white text-[14.5px] font-semibold hover:bg-[#b91f13] transition-colors"
            data-testid="nav-start-free-trial-btn"
          >
            Start Free Trial
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden h-11 w-11 inline-flex items-center justify-center rounded-full text-[#0B132B] hover:bg-slate-100/70 transition-colors"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          data-testid="nav-mobile-toggle-btn"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="nav-mobile-panel">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-base font-medium ${
                    isActive ? 'text-[#0B132B] bg-slate-100' : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="h-px bg-slate-100 my-2" />
            <Link
              to="/login"
              className="px-3 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-100"
              data-testid="nav-mobile-sign-in"
            >
              Sign In
            </Link>
            <Link
              to="/register?role=manager"
              className="mt-1 inline-flex items-center justify-center h-12 rounded-full bg-[#DA291C] text-white text-base font-semibold hover:bg-[#b91f13]"
              data-testid="nav-mobile-start-free-trial"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
